import { DestroyRef, Injectable, inject, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { Observable, catchError, of, shareReplay, tap, map } from "rxjs";

import { HearingMilestoneOverview } from "@app/case-context/models/hearing-milestone-overview.model";
import { HearingMilestoneOverviewService } from "@app/case-context/services/hearing-milestone-overview.service";

import { HearingRecordDto, ProceedingHearingCaptureDto } from "../hearing-record/hearing-record.models";
import { HearingRecordService } from "../hearing-record/hearing-record.service";

/**
 * Proceeding-scoped record cache owned by the persistent Hearing Cycle shell. Routed phase content
 * shares the in-flight/read result instead of repeating the cohesive hearing-record request.
 */
@Injectable()
export class HearingCycleContextStore {
  private readonly recordService = inject(HearingRecordService);
  private readonly milestoneService = inject(HearingMilestoneOverviewService);
  private readonly destroyRef = inject(DestroyRef);
  private loadGeneration = 0;
  private recordRequest: Observable<HearingRecordDto | null> | null = null;

  readonly scheduledProceedingId = signal<number | null>(null);
  readonly record = signal<HearingRecordDto | null>(null);
  readonly milestoneOverview = signal<HearingMilestoneOverview | null>(null);
  readonly isLoading = signal(false);
  readonly error = signal<string | null>(null);

  load(scheduledProceedingId: number): Observable<HearingRecordDto | null> {
    if (this.scheduledProceedingId() === scheduledProceedingId && this.recordRequest) {
      // A saved capture updates the signal, while shareReplay retains the original GET.
      // Routed phases must hydrate from the latest saved record when they return.
      const current = this.record();
      return current ? of(current) : this.recordRequest;
    }

    const generation = ++this.loadGeneration;
    this.scheduledProceedingId.set(scheduledProceedingId);
    this.record.set(null);
    this.milestoneOverview.set(null);
    this.error.set(null);
    this.isLoading.set(true);

    this.recordRequest = this.recordService.getRecord(scheduledProceedingId).pipe(
      map((record) => {
        if (generation !== this.loadGeneration) return null;
        if (record.scheduledProceedingId !== scheduledProceedingId) throw new Error("Hearing identity mismatch");
        return record;
      }),
      tap((record) => {
        if (!record || generation !== this.loadGeneration) return;
        this.record.set(record);
        this.isLoading.set(false);
        const personId = record.incarceratedPersonId ?? null;
        if (personId !== null && personId > 0) {
          this.loadMilestones(personId, generation);
        }
      }),
      catchError(() => {
        if (generation !== this.loadGeneration) return of(null);
        this.isLoading.set(false);
        this.error.set("The hearing context could not be loaded.");
        return of(null);
      }),
      shareReplay({ bufferSize: 1, refCount: false }),
    );

    return this.recordRequest;
  }

  refresh(): Observable<HearingRecordDto | null> {
    const id = this.scheduledProceedingId();
    if (id === null) return of(null);
    this.recordRequest = null;
    return this.load(id);
  }

  updateCapture(capture: ProceedingHearingCaptureDto): void {
    this.record.update((record) => record && record.scheduledProceedingId === capture.scheduledProceedingId
      ? { ...record, captured: { ...record.captured, capture } }
      : record);
  }

  private loadMilestones(incarceratedPersonId: number, generation: number): void {
    this.milestoneService
      .getMilestoneOverview(incarceratedPersonId)
      .pipe(
        catchError(() => of(null)),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((overview) => {
        if (generation === this.loadGeneration) this.milestoneOverview.set(overview);
      });
  }
}
